import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import '../styles/landing.css';
import logo from '../assets/logo.png';
import screenshotDashboardPng from '../assets/screenshots/new-01-dashboard.png';
import screenshotDashboardWebp from '../assets/screenshots/new-01-dashboard.webp';
import screenshotFlashcardPng from '../assets/screenshots/new-06-flashcards.png';
import screenshotFlashcardWebp from '../assets/screenshots/new-06-flashcards.webp';
import screenshotQuizPng from '../assets/screenshots/new-08-quiz.png';
import screenshotQuizWebp from '../assets/screenshots/new-08-quiz.webp';
import Footer from './common/footer';

const CAROUSEL_WORDS = [
    { label: 'Spanish', word: 'elaborar', translation: 'to develop', bar: 68, next: '4 days' },
    { label: 'German', word: 'Wissen', translation: 'knowledge', bar: 45, next: '2 days' },
    { label: 'French', word: 'apprentissage', translation: 'learning', bar: 82, next: '7 days' },
    { label: 'Russian', word: 'понимание', translation: 'understanding', bar: 30, next: '1 day' },
    { label: 'English', word: 'fluency', translation: 'fluency', bar: 90, next: '14 days' },
    { label: 'Spanish', word: 'vocabulario', translation: 'vocabulary', bar: 55, next: '3 days' },
    { label: 'French', word: 'maîtriser', translation: 'to master', bar: 40, next: '2 days' },
    { label: 'German', word: 'Sprache', translation: 'language', bar: 75, next: '6 days' },
    { label: 'Polish', word: 'uczyć się', translation: 'to study', bar: 62, next: '5 days' },
    { label: 'Polish', word: 'słuchać', translation: 'to listen', bar: 48, next: '3 days' },
];

const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const authContext = useAuth();
    const user = authContext?.user;
    const [cardIndex, setCardIndex] = useState(0);
    const [cardVisible, setCardVisible] = useState(true);

    useEffect(() => {
        const timer = setInterval(() => {
            setCardVisible(false);
            setTimeout(() => {
                setCardIndex(i => (i + 1) % CAROUSEL_WORDS.length);
                setCardVisible(true);
            }, 400);
        }, 4000);
        return () => clearInterval(timer);
    }, []);

    if (user) {
        return <Navigate to="/dashboard" replace />;
    }

    const jsonLd = [
        {
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'WeaveWords',
            url: 'https://weavewords.org',
            description: 'AI-powered language learning app that uses spaced repetition (SM-2) to build lasting vocabulary. Get AI-generated translations, contextual sentences, and personalized review scheduling. Available on web, iOS, and Android.',
            applicationCategory: 'EducationalApplication',
            operatingSystem: ['Web', 'iOS', 'Android'],
            keywords: 'AI language learning, spaced repetition app, vocabulary flashcards, SM-2 algorithm, AI flashcards, language learning AI, vocabulary builder, Anki alternative, mobile app, iOS app, Android app',
            offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USD',
            },
            featureList: [
                'AI-powered translations and contextual sentences',
                'Spaced repetition flashcards (SM-2 algorithm)',
                'AI smart scheduling based on performance',
                'Vocabulary collections and decks',
                'Multiple practice modes',
                'Progress statistics and streaks',
                'Import from Anki, Quizlet, and CSV',
                'Multi-language support',
            ],
        },
        {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'WeaveWords',
            url: 'https://weavewords.org',
            logo: 'https://weavewords.org/logo512.png',
            description: 'AI-powered spaced repetition language learning app',
        },
        {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'What is spaced repetition?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Spaced repetition is a learning technique that schedules reviews of vocabulary at increasing intervals. You review a word just before you would forget it, which dramatically improves long-term retention compared to massed practice.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What is the SM-2 algorithm?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'SM-2 is the spaced repetition algorithm originally developed for SuperMemo and made popular by Anki. WeaveWords uses SM-2 to calculate the optimal next review date for each word based on how well you remembered it.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is WeaveWords free?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Yes. WeaveWords is free to use with unlimited vocabulary collections, spaced repetition reviews, and progress tracking. A Pro plan unlocks AI-generated images, advanced statistics, and additional practice modes.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Can I import vocabulary from Anki or Quizlet?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Yes. WeaveWords supports importing vocabulary from Anki (.apkg), Quizlet exports, and plain CSV files. You can map columns to word, translation, examples, and notes during the import.',
                    },
                },
            ],
        },
    ];

    return (
        <>
            <Helmet>
                <title>WeaveWords — AI-Powered Spaced Repetition Language Learning</title>
                <meta name="description" content="Learn any language faster with AI-powered spaced repetition flashcards. WeaveWords generates translations, example sentences, and adapts review schedules using the SM-2 algorithm. Free forever. Available on web, iOS, and Android." />
                <meta name="keywords" content="AI language learning, spaced repetition app, AI flashcards, vocabulary builder, SM-2 algorithm, Anki alternative, language learning AI, vocabulary flashcards, mobile app, iOS, Android" />
                <meta property="og:title" content="WeaveWords — AI-Powered Spaced Repetition Language Learning" />
                <meta property="og:description" content="AI-powered vocabulary flashcards with smart spaced repetition. Build collections, get AI translations and example sentences, and track your progress for free." />
                <meta property="og:url" content="https://weavewords.org" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="WeaveWords — AI-Powered Spaced Repetition Language Learning" />
                <meta name="twitter:description" content="AI-powered vocabulary flashcards with smart spaced repetition. Free forever." />
                <link rel="canonical" href="https://weavewords.org" />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>

            <div className="landing-container">
                <header className="landing-header">
                    <div className="landing-header-inner">
                        <img src={logo} alt="WeaveWords" className="landing-logo" />
                        <nav className="landing-nav">
                            <button className="landing-nav-login" onClick={() => navigate('/login')}>Log in</button>
                            <button className="landing-nav-signup" onClick={() => navigate('/register')}>Get started free</button>
                        </nav>
                    </div>
                </header>

                <main>
                    {/* Hero */}
                    <section className="landing-hero">
                        <div className="landing-hero-text">
                            <div className="landing-hero-eyebrow">✦ Free · Spaced Repetition</div>
                            <h1 className="landing-h1">
                                Master any language with <span className="landing-h1-accent">spaced repetition</span>
                            </h1>
                            <p className="landing-hero-sub">
                                WeaveWords uses the proven SM-2 algorithm to schedule vocabulary
                                reviews at the perfect moment — right before you forget. Build
                                collections, practice daily, and watch your vocabulary grow.
                            </p>
                            <div className="landing-hero-actions">
                                <button className="landing-btn-primary" onClick={() => navigate('/register')}>
                                    Start learning for free
                                </button>
                                <button className="landing-btn-secondary" onClick={() => navigate('/login')}>
                                    I already have an account
                                </button>
                            </div>
                            <div className="landing-hero-social-proof">
                                <span>SM-2 algorithm</span>
                                <span className="landing-hero-social-proof-dot" />
                                <span>5+ languages</span>
                                <span className="landing-hero-social-proof-dot" />
                                <span>Web, iOS &amp; Android</span>
                                <span className="landing-hero-social-proof-dot" />
                                <span>Import from Anki &amp; Quizlet</span>
                            </div>
                        </div>
                        <div className="landing-hero-visual">
                            <div className={`landing-card-demo ${cardVisible ? 'card-visible' : 'card-hidden'}`}>
                                <div className="landing-card-label">{CAROUSEL_WORDS[cardIndex].label}</div>
                                <div className="landing-card-word">{CAROUSEL_WORDS[cardIndex].word}</div>
                                <div className="landing-card-divider" />
                                <div className="landing-card-translation">{CAROUSEL_WORDS[cardIndex].translation}</div>
                                <div className="landing-card-sr-bar">
                                    <div className="landing-card-sr-fill" style={{ width: `${CAROUSEL_WORDS[cardIndex].bar}%` }} />
                                </div>
                                <div className="landing-card-sr-label">Next review in {CAROUSEL_WORDS[cardIndex].next}</div>
                            </div>
                        </div>
                    </section>

                    {/* AI Section */}
                    <section className="landing-ai">
                        <div className="landing-ai-inner">
                            <div className="landing-ai-badge">✦ AI-Powered</div>
                            <h2 className="landing-ai-title">Smarter learning, powered by AI</h2>
                            <p className="landing-ai-sub">WeaveWords uses AI to accelerate vocabulary acquisition — not replace the work, but make every minute count.</p>
                            <div className="landing-ai-grid">
                                <div className="landing-ai-item">
                                    <div className="landing-ai-item-icon">💡</div>
                                    <div>
                                        <div className="landing-ai-item-title">Instant translations</div>
                                        <div className="landing-ai-item-desc">Add a word and get AI-suggested translations, definitions, and usage examples in seconds.</div>
                                    </div>
                                </div>
                                <div className="landing-ai-item">
                                    <div className="landing-ai-item-icon">📝</div>
                                    <div>
                                        <div className="landing-ai-item-title">Contextual sentences</div>
                                        <div className="landing-ai-item-desc">See every word used in real sentences so you learn meaning in context, not in isolation.</div>
                                    </div>
                                </div>
                                <div className="landing-ai-item">
                                    <div className="landing-ai-item-icon">🎯</div>
                                    <div>
                                        <div className="landing-ai-item-title">Smart scheduling</div>
                                        <div className="landing-ai-item-desc">The algorithm adapts to your performance — hard words come back sooner, mastered words stay out of your way.</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Features */}
                    <section className="landing-features">
                        <div className="landing-features-header">
                            <div className="landing-section-eyebrow">Features</div>
                            <h2 className="landing-section-title">Everything you need to build lasting vocabulary</h2>
                            <p className="landing-section-sub">Designed around how memory actually works — not how we wish it did.</p>
                        </div>
                        <div className="landing-features-grid">
                            <div className="landing-feature-card">
                                <div className="landing-feature-icon">🧠</div>
                                <h3>SM-2 Spaced Repetition</h3>
                                <p>The same algorithm used by Anki. Words you struggle with appear more often; words you know are scheduled less frequently.</p>
                            </div>
                            <div className="landing-feature-card">
                                <div className="landing-feature-icon">📚</div>
                                <h3>Vocabulary Collections</h3>
                                <p>Organize words into themed decks by language, topic, or level. Import from CSV, Anki, or Quizlet in seconds.</p>
                            </div>
                            <div className="landing-feature-card">
                                <div className="landing-feature-icon">🎯</div>
                                <h3>Multiple Practice Modes</h3>
                                <p>Flashcard flips, multiple-choice quizzes, and error review sessions to reinforce difficult words.</p>
                            </div>
                            <div className="landing-feature-card">
                                <div className="landing-feature-icon">📈</div>
                                <h3>Progress Tracking</h3>
                                <p>Detailed statistics, daily streaks, and heatmaps show exactly how your vocabulary is growing over time.</p>
                            </div>
                            <div className="landing-feature-card">
                                <div className="landing-feature-icon">🔊</div>
                                <h3>Text-to-Speech</h3>
                                <p>Hear correct pronunciation for every word with built-in TTS in multiple languages and voices.</p>
                            </div>
                            <div className="landing-feature-card">
                                <div className="landing-feature-icon">🌍</div>
                                <h3>Any Language</h3>
                                <p>English, Spanish, French, German, Polish, and more. WeaveWords works with any language you want to learn.</p>
                            </div>
                        </div>
                    </section>

                    {/* Mobile App */}
                    <section className="landing-mobile">
                        <div className="landing-mobile-inner">
                            <div className="landing-section-eyebrow">Available on iOS &amp; Android</div>
                            <h2 className="landing-section-title">Learn anywhere, anytime</h2>
                            <p className="landing-section-sub">Your vocabulary travels with you — review offline, get daily reminders, and stay in sync across web and mobile.</p>
                            <div className="landing-mobile-content">
                                <div className="landing-mobile-screenshots">
                                    <picture className="landing-mobile-screen landing-mobile-screen--back">
                                        <source srcSet={screenshotDashboardWebp} type="image/webp" />
                                        <img src={screenshotDashboardPng} alt="WeaveWords mobile dashboard" loading="lazy" width="180" height="390" />
                                    </picture>
                                    <picture className="landing-mobile-screen landing-mobile-screen--mid">
                                        <source srcSet={screenshotFlashcardWebp} type="image/webp" />
                                        <img src={screenshotFlashcardPng} alt="WeaveWords flashcard practice on mobile" loading="lazy" width="180" height="390" />
                                    </picture>
                                    <picture className="landing-mobile-screen landing-mobile-screen--front">
                                        <source srcSet={screenshotQuizWebp} type="image/webp" />
                                        <img src={screenshotQuizPng} alt="WeaveWords quiz mode on mobile" loading="lazy" width="180" height="390" />
                                    </picture>
                                </div>
                                <div className="landing-mobile-info">
                                    <ul className="landing-mobile-bullets">
                                        <li>Offline review queue — practice without Wi-Fi</li>
                                        <li>Daily push reminders — never miss a review</li>
                                        <li>Full sync with your web account</li>
                                        <li>Native iOS &amp; Android experience</li>
                                    </ul>
                                    <div className="landing-mobile-badges">
                                        <div className="landing-store-badge" title="Coming soon">
                                            <svg className="landing-store-badge-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.3.07 2.18.74 2.95.8 1.12-.23 2.19-.93 3.39-.84 1.44.12 2.53.72 3.24 1.83-2.98 1.79-2.27 5.7.39 6.81-.57 1.52-1.33 3-1.97 4.28zM13 3.5c.07 1.65-1.18 3-2.86 3.13C9.97 4.98 11.13 3.5 13 3.5z" fill="white"/>
                                            </svg>
                                            <div className="landing-store-badge-text">
                                                <span className="landing-store-badge-sub">Coming soon on</span>
                                                <span className="landing-store-badge-name">App Store</span>
                                            </div>
                                        </div>
                                        <div className="landing-store-badge" title="Coming soon">
                                            <svg className="landing-store-badge-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <path d="M3.18 23.76c.35.2.76.22 1.14.07l11.1-6.42-2.37-2.38L3.18 23.76zM20.5 10.5l-2.72-1.57-2.65 2.65 2.65 2.65 2.74-1.58c.78-.45.78-1.7-.02-2.15zM4.32.17C3.94.02 3.53.04 3.18.24L13.08 10.2 15.45 7.8 4.32.17zM3.18.24c-.48.28-.68.82-.68 1.37v20.78c0 .55.22 1.1.68 1.37L13.08 13.8 3.18.24z" fill="white"/>
                                            </svg>
                                            <div className="landing-store-badge-text">
                                                <span className="landing-store-badge-sub">Coming soon on</span>
                                                <span className="landing-store-badge-name">Google Play</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* How it works */}
                    <section className="landing-how">
                        <h2 className="landing-section-title">How it works</h2>
                        <div className="landing-steps">
                            <div className="landing-step">
                                <div className="landing-step-num">1</div>
                                <div>
                                    <h3>Add vocabulary</h3>
                                    <p>Create a collection and add words with translations, examples, and notes — or import an existing deck.</p>
                                </div>
                            </div>
                            <div className="landing-step">
                                <div className="landing-step-num">2</div>
                                <div>
                                    <h3>Practice daily</h3>
                                    <p>Review only the words due today. After each card, rate how well you remembered it (1–5).</p>
                                </div>
                            </div>
                            <div className="landing-step">
                                <div className="landing-step-num">3</div>
                                <div>
                                    <h3>The algorithm schedules the rest</h3>
                                    <p>SM-2 calculates the optimal next review date for every word, so you never forget and never over-study.</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* CTA */}
                    <section className="landing-cta">
                        <div className="landing-cta-inner">
                            <h2>Ready to grow your vocabulary?</h2>
                            <p>Join language learners using WeaveWords every day. Free, forever.</p>
                            <button className="landing-btn-primary landing-btn-large" onClick={() => navigate('/register')}>
                                Create your free account
                            </button>
                        </div>
                    </section>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default LandingPage;
